import { type RefObject, useLayoutEffect, useRef } from 'react'

/**
 * Tracks the `.active` child inside `containerRef` and writes its position + size onto the
 * container as CSS custom properties, so a sliding indicator element can be positioned and
 * animated purely in CSS. Both logical axes are written every measurement; the consuming
 * stylesheet reads whichever it needs (inline for a horizontal row, block for a vertical one).
 *
 * Writes onto the container:
 *   --indicator-inline-start / --indicator-inline-size
 *   --indicator-block-start  / --indicator-block-size
 *   --indicator-visible      1 when an active child exists, 0 otherwise (hides the indicator)
 *   --indicator-ready        1 when this measurement should glide, 0 to snap instantly
 *
 * The glide is reserved for active-item changes: the first mount and any resize snap instantly
 * (`--indicator-ready: 0`), so the indicator never grows in from the corner on load and never
 * lags a layout change. The stylesheet multiplies its transition duration by `--indicator-ready`.
 *
 * Offsets are read relative to the container, so the container must establish a positioning
 * context (`position: relative`) and host the indicator. The `.doss-slidable` primitive
 * (design-system/sliding-indicator.css) provides that context plus the var defaults, the marker's
 * geometry (its `::before`), and the glide — hosts add the class and style the marker's chrome.
 */
export const useSlidingIndicator = (
  containerRef: RefObject<HTMLElement | null>,
  activeKey: string | undefined,
): void => {
  const hasMounted = useRef(false)

  // Measure synchronously after DOM mutation so the indicator lands on the active child before
  // first paint, then keep it in sync with size changes (window resize, font load, reflow).
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const apply = (animate: boolean): void => {
      container.style.setProperty('--indicator-ready', animate ? '1' : '0')
      const active = container.querySelector<HTMLElement>('.active')
      if (!active) {
        container.style.setProperty('--indicator-visible', '0')
        return
      }
      container.style.setProperty('--indicator-visible', '1')
      container.style.setProperty('--indicator-inline-start', `${String(active.offsetLeft)}px`)
      container.style.setProperty('--indicator-inline-size', `${String(active.offsetWidth)}px`)
      container.style.setProperty('--indicator-block-start', `${String(active.offsetTop)}px`)
      container.style.setProperty('--indicator-block-size', `${String(active.offsetHeight)}px`)
    }

    // Snap on first mount; glide on subsequent active-item changes (this effect re-runs on activeKey).
    apply(hasMounted.current)
    hasMounted.current = true

    // Observe the container only: a content-sized row grows with its tabs, so the container
    // resize already covers width changes; vertical tabs are fixed-height. ResizeObserver fires
    // once immediately on observe(); skip that redundant first notification so only genuine later
    // resizes reposition — instantly, since a resize isn't an item change.
    let isFirstNotification = true
    const observer = new ResizeObserver(() => {
      if (isFirstNotification) {
        isFirstNotification = false
        return
      }
      apply(false)
    })
    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [containerRef, activeKey])
}
